// Lib
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

// Components
import Button from '../../../../Button';
import Feather from '../../../../Feather';
import Error from '../../Error';

// Hooks
import useImportAddress from '../../../../../pages/AddressBook/hooks/useImportAddress';

// Utils
import { fullAddressString } from '../../../../../utils/address';
import { parseAddressErrors } from '../../../../../utils/apiErrors';

// Constants
import { validationFailed } from '../../../../../constants/addressBook';

// Styles
import { useStyles } from './AddressStyles';

const Address = ({ address, isTabletMode, setEditableAddress = () => {} }) => {
  const { classes, cx } = useStyles();

  const {
    onRemoveAddress,
    onEditAddress,
    revalidateAddress,
    isLoading,
    onChangeAddress,
  } = useImportAddress(address, setEditableAddress);

  const { uspsLimitExceededMessage } = useSelector((state) => ({
    uspsLimitExceededMessage:
      state.validate.configurableMessage.usps_request_limit_exceeded_message,
  }));

  const isUspsLimitExceeded = address.usps_limit_exceeded;
  const validationErrorMessage =
    isUspsLimitExceeded && uspsLimitExceededMessage
      ? uspsLimitExceededMessage
      : validationFailed;

  const renderValidatedAddress = useCallback(() => {
    if (address.validated === undefined) {
      return;
    }
    if (address.USPS_return_text) {
      return (
        <>
          <Error error={address.USPS_return_text} />
          <Button
            className={cx(classes.retryButton, classes.retryButtonText)}
            variant="text"
            loadingIndicator="Loading..."
            onClick={revalidateAddress}
            loading={isLoading}>
            Retry
            {!isLoading && <Feather nativeColor type="arrowRight" />}
          </Button>
        </>
      );
    }
    if (!address.validated_address || address.GMOP_errors_description) {
      return (
        <>
          <Error error={validationErrorMessage} />
          <Button
            className={cx(classes.retryButton, classes.retryButtonText)}
            variant="text"
            onClick={revalidateAddress}
            loadingIndicator="Loading..."
            loading={isLoading}
            data-testid="import-address-retry-button">
            Retry
            {!isLoading && <Feather nativeColor type="arrowRight" />}
          </Button>
        </>
      );
    }
    return (
      <>
        <FormControlLabel
          className={classes.formControlLabel}
          name="validated"
          value={address.validated_address}
          control={
            <Radio
              className={classes.formControlRadio}
              onChange={onChangeAddress}
              data-testid="validated-address-radio-button"
            />
          }
          label={isTabletMode ? 'Validated Address' : address.validated_address}
        />
        {isTabletMode && <div>{address.validated_address}</div>}
      </>
    );
  }, [
    address.validated,
    address.USPS_return_text,
    address.validated_address,
    address.GMOP_errors_description,
    classes,
    cx,
    onChangeAddress,
    isTabletMode,
    revalidateAddress,
    isLoading,
    validationErrorMessage,
  ]);

  return (
    <>
      <RadioGroup
        className={classes.root}
        value={address[`${address.checked}_address`]}
        row={false}
        aria-labelledby="address-format-radio-buttons-group-label"
        name="address-format-radio-buttons-group">
        <div className={classes.enteredAddress}>
          <FormControlLabel
            className={classes.formControlLabel}
            name="original"
            value={address.original_address}
            control={
              <Radio
                className={classes.formControlRadio}
                onChange={onChangeAddress}
                data-testid="entered-address-radio-button"
              />
            }
            label={
              isTabletMode
                ? 'Entered Address'
                : fullAddressString(address.original_fields)
            }
          />
          <div className={classes.buttons}>
            <Button
              onClick={onEditAddress}
              variant="text"
              data-testid="entered-address-edit-button">
              <Feather type="edit" />
            </Button>
            <Button
              variant="text"
              onClick={onRemoveAddress}
              data-testid="entered-address-delete-button">
              <Feather type="delete" />
            </Button>
          </div>
        </div>
        {isTabletMode && (
          <div>{fullAddressString(address.original_fields)}</div>
        )}
        <div className={classes.validatedAddress}>
          {renderValidatedAddress()}
        </div>
        {address.apiError && (
          <Error error={parseAddressErrors(address.apiError)} />
        )}
      </RadioGroup>
    </>
  );
};

export default Address;
